import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import { CheckCircle, Star } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { useError } from './ErrorContext';
import { useUser } from '../contexts/UserContext';
import { getChangePasswordUrl, getMeUrl, getProfileUpdateUrl, getResendVerificationUrl, getRewardsSummaryUrl } from '../utils/UrlUtilities';
import { constructChangePasswordPayload, constructProfileUpdatePayload } from '../utils/PayloadUtils';
import { cookieOptions } from '../utils/BasicUtilities';

const isPasswordValid = (p) =>
    new RegExp(`^(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{};:'",.<>/?]).{8,}$`).test(p);

export default function ProfileSettings() {
    const CallApi = useApiCaller();
    const { showSuccess, showError } = useError();
    const { userState, setUser } = useUser();

    const [profile, setProfile] = useState(null);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [institute, setInstitute] = useState('');
    const [hideInstitute, setHideInstitute] = useState(false);
    const [saving, setSaving] = useState(false);

    const [showPasswordSection, setShowPasswordSection] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [changingPw, setChangingPw] = useState(false);
    const [resendingVerify, setResendingVerify] = useState(false);
    const [stars, setStars] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await CallApi({ url: getRewardsSummaryUrl(), method: 'GET', token: true });
            if (!cancelled && res.success) setStars(Number(res.data?.total_stars) || 0);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleResendVerification = async () => {
        setResendingVerify(true);
        const res = await CallApi({ url: getResendVerificationUrl(), method: 'POST', token: true });
        setResendingVerify(false);
        if (res.success) showSuccess('Verification email sent. Check your email Inbox/Spam folder.');
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await CallApi({ url: getMeUrl(), method: 'GET', token: true });
            if (!cancelled && res.success) {
                setProfile(res.data);
                setName(res.data.name || '');
                setPhone(res.data.phone || '');
                setInstitute(res.data.institute || '');
                setHideInstitute(Boolean(res.data.hide_institute_on_leaderboard));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveProfile = async () => {
        setSaving(true);
        const payload = constructProfileUpdatePayload({ name, phone, institute, hideInstituteOnLeaderboard: hideInstitute });
        const res = await CallApi({ url: getProfileUpdateUrl(), method: 'PUT', payload, token: true });
        setSaving(false);
        if (res.success) {
            setProfile(res.data);
            // Keep the navbar name in sync.
            const updated = { ...userState, name: res.data.name };
            setUser(updated);
            try { Cookies.set('user_state', JSON.stringify(updated), cookieOptions()); } catch (_) {}
            showSuccess('Profile updated.');
        }
    };

    const handleChangePassword = async () => {
        if (!isPasswordValid(newPassword)) {
            showError('New password must be 8+ chars with an uppercase letter, a digit, and a special character.');
            return;
        }
        setChangingPw(true);
        const payload = constructChangePasswordPayload(oldPassword, newPassword);
        const res = await CallApi({ url: getChangePasswordUrl(), method: 'POST', payload, token: true });
        setChangingPw(false);
        if (res.success) {
            setOldPassword('');
            setNewPassword('');
            setShowPasswordSection(false);
            showSuccess('Password changed successfully.');
        }
    };

    if (!profile) {
        return <div className="container py-5 text-center text-muted">Loading profile…</div>;
    }

    return (
        <div className="container py-5" style={{ maxWidth: 640 }}>
            <h2 className="fw-bold mb-4" style={{ color: '#4c1d95' }}>Profile &amp; Password Settings</h2>

            <div className="card mb-4" style={{ borderColor: '#ede9fe' }}>
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Name</label>
                        <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Email</label>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="text-muted">{profile.email}</span>
                            {profile.email_verified ? (
                                <span className="text-success d-inline-flex align-items-center fw-semibold">
                                    <CheckCircle size={16} className="me-1" /> Email Verified
                                </span>
                            ) : (
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleResendVerification}
                                    disabled={resendingVerify}
                                >
                                    {resendingVerify ? 'Sending…' : 'Verify Email'}
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Phone</label>
                        <input className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Institute</label>
                        <input className="form-control" value={institute} onChange={(e) => setInstitute(e.target.value)} />
                    </div>
                    <div className="form-check mb-3">
                        <input
                            id="hideInstitute"
                            type="checkbox"
                            className="form-check-input"
                            checked={hideInstitute}
                            onChange={(e) => setHideInstitute(e.target.checked)}
                        />
                        <label htmlFor="hideInstitute" className="form-check-label">
                            Hide my institute on public leaderboards
                        </label>
                        <small className="text-muted d-block">Your name is always shown; only the institute is hidden.</small>
                    </div>
                    <button className="btn btn-primary" onClick={handleSaveProfile} disabled={saving}>
                        {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {/* Rewards summary */}
            <div className="card mb-4" style={{ borderColor: '#ede9fe' }}>
                <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <div className="fw-semibold" style={{ color: '#4c1d95' }}>
                            <Star size={18} className="me-1" fill="#facc15" color="#facc15" />Rewards
                        </div>
                        <div className="text-muted small">
                            {stars === null ? 'Loading…' : <>You've earned <strong className="text-warning">★ {stars}</strong> {stars === 1 ? 'star' : 'stars'} so far.</>}
                        </div>
                    </div>
                    <Link to="/rewards" className="btn btn-sm btn-outline-primary">View all rewards</Link>
                </div>
            </div>

            {profile.has_password && (
            <div className="card" id="change-password" style={{ borderColor: '#ede9fe' }}>
                <div className="card-body">
                    <button className="btn btn-link p-0" onClick={() => setShowPasswordSection(s => !s)}>
                        {showPasswordSection ? 'Cancel password change' : 'Change Password'}
                    </button>
                    {showPasswordSection && (
                        <div className="mt-3">
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Current Password</label>
                                <input type="password" className="form-control" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                            </div>
                            <div className="mb-2">
                                <label className="form-label fw-semibold">New Password</label>
                                <input
                                    type="password"
                                    className={`form-control ${newPassword && !isPasswordValid(newPassword) ? 'is-invalid' : ''}`}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                                {newPassword && !isPasswordValid(newPassword) && (
                                    <small className="text-danger">Min 8 chars, with uppercase, digit, and special character.</small>
                                )}
                            </div>
                            <small className="text-muted d-block mb-3">Changing your password will log you out of all sessions.</small>
                            <button className="btn btn-primary" onClick={handleChangePassword} disabled={changingPw || !oldPassword || !newPassword}>
                                {changingPw ? 'Updating…' : 'Update Password'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            )}
        </div>
    );
}
